/* eslint-disable max-len */
/**
 * シフト編成（新） API（C3_PARTNER-358）
 * 設計書: winboard_v8_docs/docs/api/C3_PARTNER-358_シフト刷新/
 */
import {
  AutoGenerateRequest,
  BoardData,
  BoardSetting,
  ConfirmPreview,
  ConfirmRequest,
  ConfirmResult,
  CopyRequest,
  Delta,
  GeneratedOperations,
  MoveRequest,
  PeriodCreateRequest,
  PeriodData,
  RecalculateResult,
  RequiredStaffData,
  RequiredStaffRow,
  SaveRequest,
  StaffDetail,
  SubmissionsData,
  SupportCandidate,
} from 'domain/attend/shiftBoard/types';
import ApiClient from './ApiClient';

const companyCode = () => sessionStorage.getItem('loginUser.companyCode') || '';
const loginStaffCode = () => sessionStorage.getItem('loginUser.staffCode') || '';

/** A1 ボード取得 */
export const getBoard = async (
  orgCode: string, dateFrom: string, dateTo: string, businessIds: Array<string> = [], addStaffCodes: Array<string> = [],
): Promise<BoardData> => {
  const params: { [key: string]: any } = {
    loginStaffCode: loginStaffCode(), orgCode, dateFrom, dateTo,
  };
  if (businessIds.length > 0) {
    params.businessIds = businessIds.join(',');
  }
  if (addStaffCodes.length > 0) {
    params.addStaffCodes = addStaffCodes.join(',');
  }
  const response = await ApiClient.get(`/v1/shiftBoard/${companyCode()}`, params);
  return response.data;
};

/** A2 スタッフ詳細 */
export const getStaffDetail = async (
  orgCode: string, staffCode: string, targetDate: string, dateFrom?: string, dateTo?: string,
): Promise<StaffDetail> => {
  const response = await ApiClient.get(`/v1/shiftBoard/staffDetail/${companyCode()}`, {
    loginStaffCode: loginStaffCode(), orgCode, staffCode, targetDate, dateFrom, dateTo,
  });
  return response.data;
};

/** A3 一括反映 */
export const saveBulk = async (request: SaveRequest): Promise<Delta> => {
  const response = await ApiClient.postJsonData(`/v1/shiftBoard/save/${companyCode()}`, { loginStaffCode: loginStaffCode() }, request);
  return response.data;
};

/** A4 バー移動・伸縮 */
export const moveShift = async (request: MoveRequest): Promise<Delta> => {
  const response = await ApiClient.putJsonData(`/v1/shiftBoard/shift/move/${companyCode()}`, { loginStaffCode: loginStaffCode() }, request);
  return response.data;
};

/** A5 応援候補 */
export const getSupportCandidates = async (orgCode: string, sourceOrgCode: string, targetDate: string): Promise<Array<SupportCandidate>> => {
  const response = await ApiClient.get(`/v1/shiftBoard/supportCandidates/${companyCode()}`, {
    loginStaffCode: loginStaffCode(), orgCode, sourceOrgCode, targetDate,
  });
  return response.data;
};

/** A6 確定プレビュー */
export const previewConfirm = async (request: Omit<ConfirmRequest, 'dryRun'>): Promise<ConfirmPreview> => {
  const response = await ApiClient.postJsonData(`/v1/shiftBoard/confirm/${companyCode()}`, { loginStaffCode: loginStaffCode() }, { ...request, dryRun: true });
  return response.data;
};

/** A6 確定・再確定 */
export const confirmShifts = async (request: Omit<ConfirmRequest, 'dryRun'>): Promise<ConfirmResult> => {
  const response = await ApiClient.postJsonData(`/v1/shiftBoard/confirm/${companyCode()}`, { loginStaffCode: loginStaffCode() }, { ...request, dryRun: false });
  return response.data;
};

/** A8 確定解除（画面では「ロック解除」。9/24 に戻した） */
export const unconfirmShifts = async (request: Omit<ConfirmRequest, 'dryRun'>): Promise<ConfirmResult> => {
  const response = await ApiClient.postJsonData(`/v1/shiftBoard/unconfirm/${companyCode()}`, { loginStaffCode: loginStaffCode() }, request);
  return response.data;
};

/** A9 前期間コピー */
export const copyShifts = async (request: CopyRequest): Promise<GeneratedOperations> => {
  const response = await ApiClient.postJsonData(`/v1/shiftBoard/copy/${companyCode()}`, { loginStaffCode: loginStaffCode() }, request);
  return response.data;
};

/** A10 CSV */
export const downloadCsv = async (orgCode: string, dateFrom: string, dateTo: string): Promise<void> => {
  const query = {
    loginStaffCode: loginStaffCode(), orgCode, dateFrom, dateTo,
  };
  await ApiClient.downloadCsvNoConvert(`/v1/shiftBoard/csv/${companyCode()}`, query, `shift_${orgCode}_${dateFrom}_${dateTo}.csv`);
};

/** A11 回収枠 */
export const getPeriods = async (orgCode: string): Promise<Array<PeriodData>> => {
  const response = await ApiClient.get(`/v1/shiftBoard/period/${companyCode()}`, { loginStaffCode: loginStaffCode(), orgCode });
  return response.data;
};

export const createPeriod = async (request: PeriodCreateRequest): Promise<PeriodData> => {
  const response = await ApiClient.postJsonData(`/v1/shiftBoard/period/${companyCode()}`, { loginStaffCode: loginStaffCode() }, request);
  return response.data;
};

export const updatePeriod = async (
  orgCode: string, request: { periodId: string; status?: string; deadline?: string },
): Promise<PeriodData> => {
  const response = await ApiClient.putJsonData(`/v1/shiftBoard/period/${companyCode()}`, { loginStaffCode: loginStaffCode(), orgCode }, request);
  return response.data;
};

/** A12 提出状況 */
export const getSubmissions = async (orgCode: string, periodId?: string): Promise<SubmissionsData> => {
  const params: { [key: string]: string } = { loginStaffCode: loginStaffCode(), orgCode };
  if (periodId) {
    params.periodId = periodId;
  }
  const response = await ApiClient.get(`/v1/shiftBoard/submissions/${companyCode()}`, params);
  return response.data;
};

export const remindSubmissions = async (orgCode: string, periodId: string, staffCodes: Array<string>): Promise<{ reminded: number }> => {
  const response = await ApiClient.postJsonData(
    `/v1/shiftBoard/submissions/remind/${companyCode()}`, { loginStaffCode: loginStaffCode(), orgCode }, { periodId, staffCodes },
  );
  return response.data;
};

/** A14 予定人数 */
export const getRequiredStaff = async (orgCode: string, dateFrom?: string, dateTo?: string): Promise<RequiredStaffData> => {
  const params: { [key: string]: string } = { loginStaffCode: loginStaffCode(), orgCode };
  if (dateFrom && dateTo) {
    params.dateFrom = dateFrom;
    params.dateTo = dateTo;
  }
  const response = await ApiClient.get(`/v1/shiftBoard/requiredStaff/${companyCode()}`, params);
  return response.data;
};

export const saveRequiredStaffTemplate = async (
  orgCode: string, breakdownAxis: string, rows: Array<RequiredStaffRow>,
): Promise<void> => {
  await ApiClient.putJsonData(`/v1/shiftBoard/requiredStaff/template/${companyCode()}`, { loginStaffCode: loginStaffCode() }, { orgCode, breakdownAxis, rows });
};

export const saveRequiredStaffDaily = async (
  orgCode: string, targetDate: string, rows: Array<{ key: string | null; required: number }>,
): Promise<void> => {
  await ApiClient.putJsonData(`/v1/shiftBoard/requiredStaff/daily/${companyCode()}`, { loginStaffCode: loginStaffCode() }, { orgCode, targetDate, rows });
};

/** A16 ボード設定 */
export const getSetting = async (orgCode: string): Promise<BoardSetting> => {
  const response = await ApiClient.get(`/v1/shiftBoard/setting/${companyCode()}`, { loginStaffCode: loginStaffCode(), orgCode });
  return response.data;
};

export const saveSetting = async (setting: Partial<BoardSetting> & { orgCode: string }): Promise<BoardSetting> => {
  const response = await ApiClient.putJsonData(`/v1/shiftBoard/setting/${companyCode()}`, { loginStaffCode: loginStaffCode() }, setting);
  return response.data;
};

/** A18 週パターンから自動作成 */
export const autoGenerate = async (request: AutoGenerateRequest): Promise<GeneratedOperations> => {
  const response = await ApiClient.postJsonData(`/v1/shiftBoard/autoGenerate/${companyCode()}`, { loginStaffCode: loginStaffCode() }, request);
  return response.data;
};

/** A20 簡易再計算（人件費）を始める。裏で走るのですぐ返る */
export const recalculateLaborCost = async (
  orgCode: string, dateFrom: string, dateTo: string, staffCodes: Array<string> = [],
): Promise<RecalculateResult> => {
  const response = await ApiClient.postJsonData(`/v1/shiftBoard/recalculate/${companyCode()}`, { loginStaffCode: loginStaffCode() }, {
    orgCode, dateFrom, dateTo, staffCodes,
  });
  return response.data;
};

/** A20 簡易再計算の実行状況。jobId を省くとその組織の直近を返す */
export const getRecalculateStatus = async (orgCode: string, jobId?: string): Promise<RecalculateResult> => {
  const params: { [key: string]: string } = { loginStaffCode: loginStaffCode(), orgCode };
  if (jobId) {
    params.jobId = jobId;
  }
  const response = await ApiClient.get(`/v1/shiftBoard/recalculate/status/${companyCode()}`, params);
  return response.data;
};

/** A21 共通設定（common_setting）。画面ごとに設定テーブルを増やさないための共通の置き場 */
export const getCommonSetting = async (
  featureCode: string, orgCode?: string, staffCode?: string,
): Promise<{ [key: string]: any }> => {
  const response = await ApiClient.get(`/v1/commonSetting/${companyCode()}`, { featureCode, orgCode, staffCode });
  return (response.data && response.data.setting) || {};
};

export const saveCommonSetting = async (
  featureCode: string, scopeType: 'company' | 'org' | 'staff', scopeKey: string, setting: { [key: string]: any },
): Promise<void> => {
  await ApiClient.putJsonData(`/v1/commonSetting/${companyCode()}`, { loginStaffCode: loginStaffCode() }, {
    featureCode, scopeType, scopeKey, setting,
  });
};
